import { inject, OnDestroy, Pipe, PipeTransform } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

const VALIDATION_MESSAGES_TRANSLATION_KEYS: Record<string, string> = {
    Required: marker('validation.messages.required'),
    DateFormat: marker('validation.messages.dateFormat'),
    Email: marker('validation.messages.email'),
    MinLength: marker('validation.messages.minlength'),
    MaxLength: marker('validation.messages.maxlength'),
    Min: marker('validation.messages.min'),
    Max: marker('validation.messages.max'),
    NeedSelectFromList: marker('validation.messages.needSelectFromList'),
    Mismatch: marker('validation.messages.mismatch'),
    MaxFileSize: marker('validation.messages.maxFileSize'),
    Pattern: marker('validation.messages.pattern'),
};

@Pipe({
    name: 'validationMessage',
    standalone: true,
})
export class ValidationMessagePipe implements PipeTransform, OnDestroy {
    private readonly translateService: TranslateService = inject(TranslateService);
    private validationMap: Record<string, (data?: unknown, params?: unknown) => string>;

    private destroy$: Subject<void> = new Subject<void>();

    constructor() {
        this.translateService
            .get([
                VALIDATION_MESSAGES_TRANSLATION_KEYS.Required,
                VALIDATION_MESSAGES_TRANSLATION_KEYS.DateFormat,
                VALIDATION_MESSAGES_TRANSLATION_KEYS.Email,
                VALIDATION_MESSAGES_TRANSLATION_KEYS.MinLength,
                VALIDATION_MESSAGES_TRANSLATION_KEYS.MaxLength,
                VALIDATION_MESSAGES_TRANSLATION_KEYS.Min,
                VALIDATION_MESSAGES_TRANSLATION_KEYS.Max,
                VALIDATION_MESSAGES_TRANSLATION_KEYS.NeedSelectFromList,
                VALIDATION_MESSAGES_TRANSLATION_KEYS.Mismatch,
                VALIDATION_MESSAGES_TRANSLATION_KEYS.MaxFileSize,
                VALIDATION_MESSAGES_TRANSLATION_KEYS.Pattern,
            ])
            .pipe(takeUntil(this.destroy$))
            .subscribe((translations: Record<string, string>) => {
                this.validationMap = {
                    required: (): string => translations[VALIDATION_MESSAGES_TRANSLATION_KEYS.Required],
                    dateFormat: (): string => translations[VALIDATION_MESSAGES_TRANSLATION_KEYS.DateFormat],
                    email: (): string => translations[VALIDATION_MESSAGES_TRANSLATION_KEYS.Email],
                    minlength: (data: { requiredLength: number; actualLength: number }): string => {
                        const rawString: string = translations[VALIDATION_MESSAGES_TRANSLATION_KEYS.MinLength];

                        return rawString
                            .replace('{{value}}', data.requiredLength.toString())
                            .replace('{{actualValue}}', data.actualLength.toString());
                    },
                    maxlength: (data: { requiredLength: number; actualLength: number }): string => {
                        const rawString: string = translations[VALIDATION_MESSAGES_TRANSLATION_KEYS.MaxLength];

                        return rawString
                            .replace('{{value}}', data.requiredLength.toString())
                            .replace('{{actualValue}}', data.actualLength.toString());
                    },
                    min: (data: { actual: number; min: number }): string => {
                        const rawString: string = translations[VALIDATION_MESSAGES_TRANSLATION_KEYS.Min];

                        return rawString
                            .replace('{{value}}', data.min.toString())
                            .replace('{{actualValue}}', data.actual.toString());
                    },
                    max: (data: { actual: number; max: number }): string => {
                        const rawString: string = translations[VALIDATION_MESSAGES_TRANSLATION_KEYS.Max];

                        return rawString
                            .replace('{{value}}', data.max.toString())
                            .replace('{{actualValue}}', data.actual.toString());
                    },
                    needSelectFromList: (): string =>
                        translations[VALIDATION_MESSAGES_TRANSLATION_KEYS.NeedSelectFromList],
                    mismatch: (): string => translations[VALIDATION_MESSAGES_TRANSLATION_KEYS.Mismatch],
                    maxFileSize: (): string => translations[VALIDATION_MESSAGES_TRANSLATION_KEYS.MaxFileSize],
                    pattern: (): string => translations[VALIDATION_MESSAGES_TRANSLATION_KEYS.Pattern],
                };
            });
    }

    public transform(errors: ValidationErrors, params?: unknown): string {
        if (Object.keys(errors || {}).length === 0 || !this.validationMap) {
            return '';
        }

        return Object.entries(errors)
            .map(([key, value]: string[]) => {
                const messageFunction: (data?: unknown, params?: unknown) => string = this.validationMap[key];

                if (typeof messageFunction === 'function') {
                    return messageFunction(value, params);
                }

                return value;
            })
            .join('. ');
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
